import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  getReportTeams,
  getAttendanceReport,
  downloadAttendanceReport,
  emailAttendanceReport,
  type AttendanceReport,
  type ReportTeam,
  type ReportPeriod,
} from "../../services/reports";

/**
 * Raport de prezență — one month, one team, a column per training.
 *
 * The same panel serves a coach, a club admin and a coordinator: they differ
 * only in which teams the server offers them, and that is decided server-side
 * rather than by hiding options here.
 *
 * The table is horizontally scrollable with the name column pinned, because a
 * month of daily trainings is wider than any phone and the names are what you
 * need while reading across.
 */

const MONTHS = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

const MARK_STYLE: Record<string, string> = {
  x: "text-emerald-700 font-bold",
  o: "text-rose-600 font-bold",
  m: "text-amber-600 font-bold",
  "-": "text-slate-300",
};

export default function AttendanceReportPanel({
  compact = false,
  initialTeamId,
}: {
  compact?: boolean;
  /** Preselect a team — used when arriving from that team's page. */
  initialTeamId?: string | null;
}) {
  const now = new Date();

  const [teams, setTeams] = useState<ReportTeam[]>([]);
  const [sports, setSports] = useState<{ id: string; name: string }[]>([]);
  const [months, setMonths] = useState<ReportPeriod[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);

  const [sportId, setSportId] = useState("");
  const [teamQuery, setTeamQuery] = useState("");
  const [teamId, setTeamId] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [report, setReport] = useState<AttendanceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"download" | "email" | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    getReportTeams()
      .then((data) => {
        setTeams(data.teams);
        setSports(data.sports);
        setMonths(data.months);
        // A team handed to us wins; otherwise preselect only when there is
        // exactly one and the choice is therefore not a choice.
        if (initialTeamId && data.teams.some((t) => t.id === initialTeamId)) setTeamId(initialTeamId);
        else if (data.teams.length === 1) setTeamId(data.teams[0].id);
        // Start on the most recent month that actually has sessions, rather
        // than on today's, which early in a month is empty.
        if (data.months.length > 0) {
          setYear(data.months[0].year);
          setMonth(data.months[0].month);
        }
      })
      .catch(() => setError("Nu am putut încărca echipele."))
      .finally(() => setLoadingTeams(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTeamId]);

  const visibleTeams = useMemo(
    () => {
      const q = teamQuery.trim().toLowerCase();
      return teams.filter((t) => {
        if (sportId && t.sport_id !== sportId) return false;
        if (!q) return true;
        return [t.name, t.age_group, t.club_name].filter(Boolean).join(" ").toLowerCase().includes(q);
      });
    },
    [teams, sportId, teamQuery]
  );

  // A platform admin sees every club's teams, so the club has to be on the
  // option or two "U14" entries are indistinguishable. For everyone else the
  // club is implied and saying it is noise.
  const manyClubs = useMemo(() => new Set(teams.map((t) => t.club_id)).size > 1, [teams]);

  // A sport filter that excludes the chosen team would otherwise leave a
  // stale selection generating a report for a team no longer in the list.
  useEffect(() => {
    if (teamId && !visibleTeams.some((t) => t.id === teamId)) setTeamId("");
  }, [visibleTeams, teamId]);

  const years = useMemo(() => {
    const fromData = Array.from(new Set(months.map((m) => m.year)));
    const current = now.getFullYear();
    return Array.from(new Set([...fromData, current, current - 1])).sort((a, b) => b - a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  const generate = useCallback(async () => {
    if (!teamId) {
      toast.error("Alege echipa.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setReport(await getAttendanceReport({ teamId, year, month }));
    } catch (e: any) {
      setReport(null);
      setError(e?.response?.data?.error || "Nu am putut genera raportul.");
    }
    setLoading(false);
  }, [teamId, year, month]);

  const download = async () => {
    if (!report) return;
    setBusy("download");
    try {
      await downloadAttendanceReport({ teamId, year, month });
    } catch {
      toast.error("Descărcarea nu a reușit.");
    }
    setBusy(null);
  };

  const sendEmail = async () => {
    if (!report) return;
    setBusy("email");
    try {
      const res = await emailAttendanceReport({ teamId, year, month, to: emailTo.trim() || undefined });
      toast.success(`Raportul a plecat către ${res.to}.`);
      setShowEmail(false);
      setEmailTo("");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Trimiterea nu a reușit.");
    }
    setBusy(null);
  };

  const selectCls =
    "w-full px-3 py-2 text-base bg-white border rounded-lg border-slate-300 text-slate-700 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

  if (loadingTeams) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 rounded-full border-indigo-600 animate-spin border-t-transparent" />
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="p-8 text-center bg-white border rounded-2xl border-slate-200">
        <p className="font-medium text-slate-700">Nicio echipă disponibilă</p>
        <p className="mt-1 text-sm text-slate-500">
          Rapoartele de prezență se generează pe echipă. Când ai o echipă alocată, apare aici.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="p-4 bg-white border rounded-2xl border-slate-200">
        <div className={`grid gap-3 ${compact ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-5"}`}>
          {sports.length > 1 && (
            <div className={compact ? "col-span-2" : ""}>
              <label className="block mb-1 text-xs font-semibold text-slate-500">Sport</label>
              <select value={sportId} onChange={(e) => setSportId(e.target.value)} className={selectCls}>
                <option value="">Toate sporturile</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className={compact ? "col-span-2" : "lg:col-span-2"}>
            <label className="block mb-1 text-xs font-semibold text-slate-500">
              Grupă / echipă
              {teams.length > 20 && (
                <span className="ml-1 font-normal text-slate-400">({visibleTeams.length})</span>
              )}
            </label>
            {teams.length > 20 && (
              // Past a couple of dozen, scrolling a dropdown is the problem the
              // list page had. Type two letters instead.
              <input
                value={teamQuery}
                onChange={(e) => setTeamQuery(e.target.value)}
                placeholder="Caută echipa..."
                className={`${selectCls} mb-2`}
              />
            )}
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className={selectCls}>
              <option value="">Alege...</option>
              {visibleTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {manyClubs ? `${t.club_name} — ` : ""}
                  {t.name}
                  {t.age_group ? ` · ${t.age_group}` : ""}
                  {t.sport_name && sports.length > 1 ? ` · ${t.sport_name}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 text-xs font-semibold text-slate-500">An</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={selectCls}>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 text-xs font-semibold text-slate-500">Luna</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={selectCls}>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button
            onClick={generate}
            disabled={loading || !teamId}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? "Se generează..." : "Generează raportul"}
          </button>

          {report && (
            <>
              <button
                onClick={download}
                disabled={busy !== null}
                className="px-4 py-2 text-sm font-semibold border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {busy === "download" ? "Se descarcă..." : "Descarcă (CSV)"}
              </button>
              <button
                onClick={() => setShowEmail((v) => !v)}
                disabled={busy !== null}
                className="px-4 py-2 text-sm font-semibold border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Trimite pe email
              </button>
            </>
          )}
        </div>

        {showEmail && report && (
          <div className="flex flex-wrap items-end gap-2 p-3 mt-3 rounded-xl bg-slate-50">
            <div className="flex-1 min-w-[220px]">
              <label className="block mb-1 text-xs font-semibold text-slate-500">
                Trimite către (gol = adresa ta)
              </label>
              <input
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                inputMode="email"
                placeholder="nume@exemplu.ro"
                className={selectCls}
              />
            </div>
            <button
              onClick={sendEmail}
              disabled={busy !== null}
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy === "email" ? "Se trimite..." : "Trimite"}
            </button>
          </div>
        )}

        {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      </div>

      {/* The report */}
      {report && (
        <div className="bg-white border rounded-2xl border-slate-200">
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="font-bold text-slate-800">
              {report.team.name}
              {report.team.age_group ? ` · ${report.team.age_group}` : ""}
              {report.team.sport ? ` · ${report.team.sport}` : ""}
            </p>
            <p className="text-sm text-slate-500">
              {report.period.label} · {report.totals.athletes} sportivi · {report.totals.sessions} antrenamente
            </p>
            <p className="mt-1 text-[11px] text-slate-400">{report.legend}</p>
          </div>

          {report.sessions.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-medium text-slate-700">Nicio sesiune în luna aleasă</p>
              <p className="mt-1 text-sm text-slate-500">
                Alege altă lună, sau verifică dacă antrenamentele au fost programate în aplicație.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50">
                    {/* Pinned: the names are what you read across by. */}
                    <th className="sticky left-0 z-10 px-3 py-2 font-semibold text-left border-b bg-slate-50 border-slate-200 text-slate-600 min-w-[160px]">
                      Nume și prenume
                    </th>
                    <th className="px-3 py-2 font-semibold text-left border-b border-slate-200 text-slate-600 whitespace-nowrap">
                      An
                    </th>
                    <th className="px-3 py-2 font-semibold text-left border-b border-slate-200 text-slate-600 whitespace-nowrap">
                      Telefon
                    </th>
                    {report.sessions.map((s) => (
                      <th
                        key={s.id}
                        title={s.title}
                        className="px-2 py-2 font-semibold text-center border-b border-slate-200 text-slate-600 whitespace-nowrap"
                      >
                        <span className="block text-[11px] text-slate-400">{s.day}</span>
                        {s.date}
                      </th>
                    ))}
                    <th className="px-3 py-2 font-semibold text-center border-b border-slate-200 text-slate-600 whitespace-nowrap">
                      Prezență
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.athlete_id} className="hover:bg-slate-50/60">
                      <td className="sticky left-0 z-10 px-3 py-2 bg-white border-b border-slate-100">
                        <span className="font-medium text-slate-800">{r.nume}</span>{" "}
                        <span className="text-slate-600">{r.prenume}</span>
                      </td>
                      <td className="px-3 py-2 border-b border-slate-100 text-slate-600 tabular-nums">
                        {r.birth_year ?? "—"}
                      </td>
                      <td className="px-3 py-2 border-b border-slate-100 text-slate-600 whitespace-nowrap">
                        {r.phone ? (
                          <>
                            {r.phone}
                            {r.phone_of === "părinte" && (
                              <span className="block text-[11px] text-slate-400">părinte</span>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {report.sessions.map((s) => (
                        <td
                          key={s.id}
                          className={`px-2 py-2 text-center border-b border-slate-100 ${MARK_STYLE[r.marks[s.id] || "-"]}`}
                        >
                          {r.marks[s.id] || "-"}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-center border-b border-slate-100 whitespace-nowrap">
                        <span className="font-semibold text-slate-800 tabular-nums">{r.rate}%</span>
                        <span className="block text-[11px] text-slate-400">
                          {r.present}/{r.present + r.absent + r.excused}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
